/**
 * Factura Electrónica SV - Brand Configuration
 * Guía de Identidad Visual
 */

export const brandConfig = {
  name: "Factura Electrónica SV",
  tagline: "PREMIUM DTE",

  // Typography
  fonts: {
    primary: "Inter",
    weights: {
      light: 300,
      regular: 400,
      semibold: 600,
      extrabold: 800,
    },
    usage: {
      light: "Subtítulos finos o detalles grandes de marketing",
      regular: "Texto corrido, párrafos, tablas de datos y descripciones",
      semibold: "Botones, navegación, encabezados de tabla y énfasis",
      extrabold: "Titulares principales, números de impacto y Logotipo",
    },
  },

  // Color Palette
  colors: {
    haciendaBlue: {
      hex: "#1E3A8A",
      tailwind: "blue-900",
      usage: "Color principal, logotipo, headers",
    },
    premiumGold: {
      hex: "#D97706",
      tailwind: "amber-600",
      usage: "Acentos, checkmarks, tagline",
    },
    validGreen: {
      hex: "#10B981",
      tailwind: "emerald-500",
      usage: "Estado transmitido, éxito",
    },
    paperWhite: {
      hex: "#F8FAFC",
      tailwind: "slate-50",
      usage: "Fondos UI, documentos",
    },
    // Dark mode variants
    darkMode: {
      primary: "#FFFFFF",
      accent: "#FBBF24", // amber-400
      circleOpacity: 0.4,
    },
  },

  // Logo SVG paths
  logo: {
    // Icon paths (viewBox: 0 0 80 80)
    icon: {
      leftBracket: "M40 10H20C14.4772 10 10 14.4772 10 20V60C10 65.5228 14.4772 70 20 70H40",
      rightBracket: "M50 70H60C65.5228 70 70 65.5228 70 60V20C70 14.4772 65.5228 10 60 10H50",
      checkmark: "M25 40L35 50L55 30",
      dashedCircle: { cx: 40, cy: 40, r: 32 },
    },
    // Favicon/App icon (with background)
    favicon: {
      background: { rx: 16, fill: "#1e3a8a" },
      leftBracket: "M40 15H25C20 15 17 19 17 23V57C17 61 20 65 25 65H40",
      rightBracket: "M47 65H55C60 65 63 61 63 57V23C63 19 60 15 55 15H47",
      checkmark: "M28 40L36 48L52 32",
    },
    // Stroke widths
    strokeWidths: {
      brackets: 4,
      checkmark: 5,
      dashedCircle: 2,
      faviconBrackets: 3,
      faviconCheckmark: 4,
    },
  },

  // UI Guidelines
  ui: {
    borderRadius: "0.75rem",
    shadows: {
      card: "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
      button: "0 10px 25px -5px rgba(30, 58, 138, 0.25)",
    },
    glass: {
      background: "rgba(255, 255, 255, 0.8)",
      border: "rgba(30, 58, 138, 0.1)",
      backdropBlur: "12px",
    },
  },
} as const;

/**
 * Identidad de Dev IA — quien desarrolla Factura Premium.
 *
 * Es una marca distinta de la del producto y no comparte paleta: Factura Premium usa el azul
 * Hacienda porque habla de cumplimiento fiscal; Dev IA usa su cian porque habla de quien
 * construye. Mezclarlas haría que la página de la empresa pareciera otra pantalla del producto,
 * y lo que tiene que transmitir es lo contrario — que hay una casa de desarrollo detrás.
 *
 * Los colores salen de los archivos de marca originales, no de una aproximación a ojo.
 */
export const devIaBrand = {
  name: "Dev IA",
  tagline: "Tecnología que forma y construye",

  /**
   * Tomados del Tailwind del propio sitio de Dev IA, no de una aproximación a ojo.
   *
   * **Dev IA es una marca oscura**: fondo casi negro con acento cian. Lo contrario de Factura
   * Premium, que es azul y claro. La diferencia es deliberada y hay que respetarla — si la
   * página de la empresa usara la paleta del producto, parecería otra pantalla del producto.
   */
  colors: {
    background: "#050505",
    surface: "#0d0d0d",
    border: "#1a1a1a",
    /** Cian de la estrella y de «IA». El acento de la marca. */
    accent: "#00D1FF",
    accentDim: "#00a8cc",
    foreground: "#FFFFFF",
    muted: "#6b7280",
    mutedLight: "#9ca3af",
    /** Azul del sello. Solo para la identidad legal: titular, NIT, NRC. */
    sealBlue: "#1D3F7B",
  },

  assets: {
    /**
     * Logotipo completo: estrella + «Dev» en blanco + «IA» en cian.
     * **Va sobre fondo oscuro** — «Dev» es blanco.
     */
    lockup: "/marca/devia-lockup.svg",
    /** Variante para fondo claro. */
    lockupLight: "/marca/devia-header-preview-white.svg",
    /** Solo la estrella. */
    isotipo: "/marca/devia-logo.svg",
    /** Sello circular con titular, NIT y NRC. */
    seal: "/marca/devia-sello.png",
  },
} as const;

// Type exports
export type BrandColors = typeof brandConfig.colors;
export type BrandFonts = typeof brandConfig.fonts;
export type DevIaBrand = typeof devIaBrand;
